import { forwardRef, useCallback } from 'react';
import { useState } from 'react';
import styled from '@emotion/styled';
import { colors, fontSizes } from '@anyroad/theme';
import type { HTMLProps, ElementType } from 'react';

type TextAreaProps = HTMLProps<HTMLTextAreaElement> & {
  dataTestId?: string;
  label?: string;
  /** The label. */
  showCount?: boolean;
};

const StyledLabel = styled.label({
  display: 'block',
  margin: '0px 0px 10px',
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: fontSizes.medium,
  color: colors.text.primary,
  lineHeight: '20px',
  fontWeight: '600',
  width: 'initial',
});
const StyledTextArea = styled.textarea({
  width: '100%',
  padding: '14px 15px',
  boxSizing: 'border-box',
  border: `1px solid ${colors.borderAction}`,
  fontSize: fontSizes.medium,
  fontFamily: 'inherit',
});
const StyledCount = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  fontSize: fontSizes.small,
  color: colors.text.secondary,
});

/**
 * TextArea component.
 *
 * @type {React.FC<TextAreaProps>}
 * @returns {React.ReactElement} The text-area.
 * @example
 * import { TextArea } from './TextArea';
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    { as, label, dataTestId, onChange, showCount, ...rest }: TextAreaProps,
    ref
  ) => {
    const [value, setValue] = useState('');
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setValue(e.target.value);
        onChange?.(e);
      },
      [onChange]
    );

    return (
      <div>
        {label ? <StyledLabel htmlFor={label}>{label}</StyledLabel> : null}
        <StyledTextArea
          as={as as ElementType<any>}
          data-testid={dataTestId}
          id={label}
          onChange={handleChange}
          ref={ref}
          {...rest}
        />
        {showCount && rest.maxLength ? (
          <StyledCount>
            {value.length}
            {'/'}
            {rest.maxLength}
          </StyledCount>
        ) : null}
      </div>
    );
  }
);
TextArea.displayName = 'TextArea';
